import { useCallback, useEffect, useState } from "react";

import { IImportContext, ISelectState, IsSelected, SelectItem, SelectItemData } from "./import-context";
import { find, remove, forEach, filter, uniqBy } from "lodash";
import { useDealActions, useDealIdSelector } from "store";
import { useActionHook } from "hooks";
import { IFolderTemplate, ITemplate } from "store/types";

export const useImportContextHook = (templateValue: ITemplate): IImportContext => {
  const dealId = useDealIdSelector();
  const [state, setState] = useState<ISelectState>({ folders: [], files: [], forms: [], documents: [] });
  const { importFolderAction, getDealChunksAction } = useDealActions();
  const { action: getDealParts } = useActionHook(getDealChunksAction);
  const { action: importFolder, loading: importLoading } = useActionHook(importFolderAction);
  const [folders, setFolders] = useState<IFolderTemplate[]>([]);

  useEffect(() => {
    setState({ folders: [], files: [], forms: [], documents: [] });
    templateValue && templateValue.sections?.length && setFolders(templateValue?.sections);
  }, [templateValue]);

  const selectFolder = useCallback(
    (id: number, isSelected: boolean) => {
      let stateCopy = { ...state };

      const deleteFoldersById = (stateCopy: ISelectState, targetId: number) => {
        function findFolderById(id: number) {
          return find(stateCopy.folders, { id });
        }

        const deleteFolderById = (id: number) => {
          remove(stateCopy.folders, { id });
        };

        const deleteDocumentsFilesFolders = (parentId: number) => {
          remove(stateCopy.documents, { parentId });
          remove(stateCopy.files, { parentId });
          remove(stateCopy.forms, { parentId });
        };

        const deleteFoldersRecursively = (parentId: number) => {
          const foldersToDelete = filter(stateCopy.folders, { parentId });

          forEach(foldersToDelete, (folder) => {
            deleteFoldersRecursively(folder.id);
            deleteFolderById(folder.id);
            deleteDocumentsFilesFolders(folder.id);
          });
        };

        const rootFolder = findFolderById(targetId);

        if (rootFolder) {
          deleteFoldersRecursively(targetId);
          deleteFolderById(targetId);
          deleteDocumentsFilesFolders(targetId);
        }
      };

      // need to find all parents folders and select them
      const findParentsFoldersAndSelect = (id: number) => {
        const current = folders.find((item) => item.id === id);
        if (current) {
          const folders: SelectItem[] = [...stateCopy.folders, { id: current.id, parentId: current.parent_id || null }];
          const uniqFolders = uniqBy(folders, "id");
          stateCopy = { ...stateCopy, folders: [...uniqFolders] };
          current.parent_id && findParentsFoldersAndSelect(current.parent_id);
        }
      };
      //need to find all children folders and select them
      const findChildrenFoldersAndSelect = (id: number) => {
        const childFolders = folders.filter((item) => item.parent_id === id);
        childFolders.forEach((item) => {
          const folders: SelectItem[] = [...stateCopy.folders, { id: item.id, parentId: item.parent_id || null }];
          const uniqFolders = uniqBy(folders, "id");
          stateCopy = { ...stateCopy, folders: [...uniqFolders] };
          selectFolder(item.id, false);
          findChildrenFoldersAndSelect(item.id);
        });
      };

      // Handle unselect
      if (isSelected) {
        //need to find all children folders and unselect them, with nested folders and files
        deleteFoldersById(stateCopy, id);
      } else {
        // Handle Select
        findParentsFoldersAndSelect(id);
        findChildrenFoldersAndSelect(id);
        //need to find all children files and select them

        const files: SelectItem[] = stateCopy.files;
        const forms: SelectItem[] = stateCopy.forms;
        const currentFolder = folders.find((item) => item.id === id);
        currentFolder &&
          currentFolder?.details?.files.forEach((item) => {
            !files.some((file) => file.id === item.id) &&
              files.push({ id: item.id, parentId: currentFolder.id || null });
          });
        stateCopy = { ...stateCopy, files: files };

        currentFolder &&
          currentFolder?.details?.forms.forEach((item) => {
            !forms.some((form) => form.id === item.id) &&
              forms.push({ id: item.id, parentId: currentFolder.id || null });
          });
        stateCopy = { ...stateCopy, forms: forms };
      }
      setState(stateCopy);
    },
    [state, folders]
  );

  const selectItem = useCallback(
    ({ key, item }: SelectItemData) => {
      const isSelected = state[key].some((el) => el.id === item.id);
      if (key === "folders") {
        return selectFolder(item.id, isSelected);
      }
      let stateCopy = { ...state };

      const findParentsFolders = (id: number) => {
        const current = folders.find((item) => item.id === id);
        if (current) {
          const folders: SelectItem[] = [...stateCopy.folders, { id: current.id, parentId: current.parent_id || null }];
          const uniqFolders = uniqBy(folders, "id");
          stateCopy = { ...stateCopy, folders: [...uniqFolders] };
          current.parent_id && findParentsFolders(current.parent_id);
        }
      };
      item.parentId && findParentsFolders(item.parentId);
      if (isSelected) {
        stateCopy[key] = stateCopy[key].filter((el) => el.id !== item.id);
      } else {
        stateCopy[key] = [...state[key], item];

        if (item.parentId && !stateCopy.folders.some((el) => el.id === item.parentId)) {
          stateCopy.folders = [...stateCopy.folders, { id: item.parentId, parentId: null }];
        }
      }
      setState(stateCopy);
    },
    [state, folders, selectFolder]
  );

  const isSelected: IsSelected = useCallback(
    ({ key, id }) => {
      return state[key].some((item) => item.id === id);
    },
    [state]
  );

  const onSubmit = useCallback(async () => {
    const payload = {
      sections: state.folders.reduce((acc, item) => {
        return {
          ...acc,
          [item.id]: {
            parent_id: item.parentId,
            // documents: state.documents.filter((el) => el.parentId === item.id).map((el) => el.id),
            forms: state.forms.filter((el) => el.parentId === item.id).map((el) => el.id),
            files: state.files.filter((el) => el.parentId === item.id).map((el) => el.id)
          }
        };
      }, {})
    };

    dealId && (await importFolder(payload, dealId, templateValue.id));
    dealId && (await getDealParts(dealId));

    setState({ folders: [], files: [], forms: [], documents: [] });
  }, [state, dealId]);

  return {
    importLoading,
    onSubmit,
    state,
    selectItem,
    isSelected
  };
};
